import {Map} from 'immutable';
import {getProductKeys} from '~/utils/url';

import {DEFAULT_COLOR_ID} from '~/constants/ProductConstants';
import {
  DEFAULT_GOAL_QUANTITY,
  GOAL_QUANTITY_OPTIONS,
  MINIMUM_PRODUCT_PRICE
} from '~/constants/FundraisingConstants';
// types
import FundraisingTypes from '~/constants/types/FundraisingTypes';
import ProductTypes from '~/constants/types/ProductTypes';
import DesignTypes from '~/constants/types/DesignTypes';
import {makeUndoable} from '~/utils/undoable';

export function initialState() {
  let {colorId} = getProductKeys();
  colorId = colorId || DEFAULT_COLOR_ID;
  return Map({
    goalQuantity: DEFAULT_GOAL_QUANTITY,
    salePrices: Map([[colorId, 0]]),
    printMinimums: Map([
      [colorId, {printMinimumQuantity: 0, errorMessage: null}]
    ]),
    profits: Map([[colorId, 0]]),
    minProfit: 0,
    maxProfit: 0,
    skipOverview: false,
    fetching: false
  });
}

export function fundraising(state = initialState(), action) {
  switch (action.type) {
    case FundraisingTypes.SET_SKIP_OVERVIEW: {
      return state.set('skipOverview', true);
    }
    case FundraisingTypes.UPDATE_GOAL_QUANTITY: {
      const quantity = parseInt(action.payload, 10);
      if (GOAL_QUANTITY_OPTIONS.includes(quantity)) {
        return state.set('goalQuantity', quantity);
      } else {
        return state;
      }
    }
    case FundraisingTypes.UPDATE_SALE_PRICE: {
      const {colorId, price} = {
        colorId: action.payload.colorId,
        price: parseFloat(action.payload.price)
      };

      if (price >= MINIMUM_PRODUCT_PRICE) {
        return state.setIn(['salePrices', colorId], price);
      } else {
        return state;
      }
    }
    case FundraisingTypes.UPDATE_PRINT_MINIMUM: {
      const {colorId, printMinimumQuantity, errorMessage} = {
        colorId: action.payload.colorId,
        printMinimumQuantity: parseInt(action.payload.printMinimumQuantity, 10),
        errorMessage: action.payload.errorMessage
      };
      return state.setIn(['printMinimums', colorId], {
        printMinimumQuantity,
        errorMessage
      });
    }
    case FundraisingTypes.UPDATE_PROFIT: {
      const {colorId, profit} = {
        colorId: action.payload.colorId,
        profit: parseFloat(action.payload.profit)
      };
      return state.setIn(['profits', colorId], profit);
    }
    case FundraisingTypes.UPDATE_ESTIMATED_PROFIT: {
      const {minProfit, maxProfit} = {
        minProfit: parseFloat(action.payload.minProfit),
        maxProfit: parseFloat(action.payload.maxProfit)
      };
      if (minProfit >= 0 && maxProfit >= minProfit) {
        return state.set('minProfit', minProfit).set('maxProfit', maxProfit);
      } else {
        return state;
      }
    }
    case FundraisingTypes.FETCH: {
      return state.set('fetching', true);
    }
    case FundraisingTypes.FETCH_SUCCESS: {
      return state.set('fetching', false);
    }
    case FundraisingTypes.FETCH_FAILED: {
      return state.set('fetching', false);
    }
    case DesignTypes.FETCH_SUCCESS: {
      const products = action.payload.products;
      let newState = state
        .removeIn(['salePrices', DEFAULT_COLOR_ID])
        .removeIn(['printMinimums', DEFAULT_COLOR_ID])
        .removeIn(['profits', DEFAULT_COLOR_ID]);
      products.forEach(p => {
        newState = newState
          .setIn(['salePrices', p.colorId], 0)
          .setIn(['printMinimums', p.colorId], 0)
          .setIn(['profits', p.colorId], 0);
      });
      return newState;
    }
    case ProductTypes.ADD_PRODUCT: {
      const {products} = action.payload;
      let newState = state;
      products.forEach(p => {
        newState = newState
          .setIn(['salePrices', p.colorId], 0)
          .setIn(['printMinimums', p.colorId], 0)
          .setIn(['profits', p.colorId], 0);
      });
      return newState;
    }
    case ProductTypes.SWAP_PRODUCT: {
      const products = action.payload.nextColorIds;
      let newState = state
        .set('salePrices', Map([]))
        .set('printMinimums', Map([]))
        .set('profits', Map([]));
      Object.keys(products).forEach(productKey => {
        newState = newState
          .setIn(['salePrices', products[productKey]], 0)
          .setIn(['printMinimums', products[productKey]], 0)
          .setIn(['profits', products[productKey]], 0);
      });
      return newState;
    }
    case ProductTypes.DELETE_PRODUCT: {
      const {colorId} = action.payload;
      return state
        .removeIn(['salePrices', colorId])
        .removeIn(['printMinimums', colorId])
        .removeIn(['profits', colorId]);
    }
    case ProductTypes.CHANGE_COLOR: {
      const {previousColorId} = action.payload;
      return state
        .removeIn(['salePrices', previousColorId])
        .removeIn(['printMinimums', previousColorId])
        .removeIn(['profits', previousColorId]);
    }
    default:
      return state;
  }
}

export default makeUndoable(fundraising);
